import React, { useEffect, useRef } from "react";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { registerRMUser } from "../../redux/user/userSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BiArrowBack } from "react-icons/bi";
import { LuX } from "react-icons/lu";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  modalInputSx,
  PROFILE_FONT,
} from "../../constants/layout";
import { primaryButtonSx } from "../AssetListing/assetListingShared";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";

type FormValues = {
  firstName: string;
  email: string;
  mobile: string;
  userType: number;
};

type ModalFieldProps = {
  label: string;
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  error?: string;
  touched?: boolean | string;
};

const ModalField = ({
  label,
  id,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  touched,
}: ModalFieldProps) => {
  const hasError = !!(error && touched);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: "block",
          fontFamily: PROFILE_FONT,
          fontSize: 14,
          fontWeight: 500,
          color: hasError ? "#d32f2f" : DARK_NAVY,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        component="input"
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        sx={{
          ...modalInputSx,
          ...(hasError
            ? {
                borderColor: "#d32f2f",
                "&:focus": { borderColor: "#d32f2f", boxShadow: "none" },
              }
            : {}),
        }}
      />
      {hasError ? (
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
});

const getInitialValues = (): FormValues => ({
  firstName: "",
  email: "",
  mobile: "",
  userType: 2,
});

export type RegisterFormProps = {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  variant?: "page" | "modal";
};

const RegisterForm = ({
  open = false,
  onClose,
  onSuccess,
  variant = "modal",
}: RegisterFormProps) => {
  const dispatch = useDispatch<any>();
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const navigate = useNavigate();
  const isPage = variant === "page";

  const resetFormState = () => {
    formikRef.current?.resetForm({ values: getInitialValues() });
  };

  useEffect(() => {
    if (!open && !isPage) resetFormState();
  }, [open, isPage]);

  const handleClose = () => {
    resetFormState();
    if (onClose) {
      onClose();
    } else {
      navigate("/rm");
    }
  };

  const handleSuccess = () => {
    resetFormState();
    toast.success("Relation Manager registered successfully");
    onSuccess?.();
    if (!isPage) {
      onClose?.();
    } else {
      navigate("/rm");
    }
  };

  const formFields = (
    <Formik
      innerRef={formikRef}
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await dispatch(registerRMUser({ ...values, userType: 2 })).unwrap();
          handleSuccess();
        } catch (error: any) {
          const errorMessage = error.message || "An unknown error occurred";
          toast.error("Error: " + errorMessage);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, handleChange, handleSubmit, errors, touched, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <ModalField
                label="First Name"
                id="firstName"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                error={errors.firstName}
                touched={touched.firstName}
              />
              <ModalField
                label="Email"
                id="email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Enter email address"
                error={errors.email}
                touched={touched.email}
              />
              <ModalField
                label="Mobile Number"
                id="mobile"
                name="mobile"
                value={values.mobile}
                onChange={handleChange}
                placeholder="Enter mobile number"
                error={errors.mobile}
                touched={touched.mobile}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
              {!isPage ? (
                <Button
                  onClick={handleClose}
                  sx={{
                    fontFamily: PROFILE_FONT,
                    textTransform: "none",
                    color: DARK_NAVY_LIGHT,
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={isSubmitting}
                sx={{ ...primaryButtonSx, px: 3, py: 1.25, minWidth: 140 }}
              >
                Register
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );

  if (isPage) {
    return (
      <Box sx={{ width: "100%", fontFamily: PROFILE_FONT }}>
        <Box
          sx={{ display: "flex", justifyContent: "flex-start", mb: 2, cursor: "pointer", color: DARK_NAVY }}
          onClick={handleClose}
        >
          <BiArrowBack size={24} />
        </Box>
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            color: DARK_NAVY,
            mb: 3,
          }}
        >
          Register Relation Manager
        </Typography>
        {formFields}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          fontFamily: PROFILE_FONT,
          boxShadow: CARD_SHADOW,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: PROFILE_FONT,
          fontWeight: 700,
          fontSize: 20,
          color: DARK_NAVY,
          pr: 6,
          pb: 1,
        }}
      >
        Add Relation Manager
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 13,
            color: DARK_NAVY_LIGHT,
            fontWeight: 400,
            mt: 0.5,
          }}
        >
          Register a new relation manager with their basic details.
        </Typography>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: DARK_NAVY_LIGHT,
            "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
          }}
        >
          <LuX size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {formFields}
      </DialogContent>
    </Dialog>
  );
};

export default RegisterForm;
